import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  useCheckPaymentStatusQuery,
  useGetCustomerInfoQuery,
} from '../../redux/payments/payments';
import {
  Box,
  VStack,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';

export const PaymentStatusScreen: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const session_id = searchParams.get('session_id');

  const { data, isLoading, isError } = useCheckPaymentStatusQuery(session_id as string);

  const { refetch } = useGetCustomerInfoQuery();

  useEffect(() => {
    refetch();
  }, [session_id, refetch]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Alert
          status="error"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Error
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            There was an error checking the payment status. Please try again later.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }
  //@ts-ignore
  const checkoutSession = data?.checkoutSession;

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
      <VStack
        spacing={4}
        align="stretch"
        bg="white"
        p={6}
        borderRadius="md"
        boxShadow="md"
        maxWidth="400px"
      >
        {checkoutSession?.payment_status === 'paid' ? (
          <CheckCircleIcon boxSize="50px" color="green.500" alignSelf="center" />
        ) : (
          <WarningIcon boxSize="50px" color="orange.500" alignSelf="center" />
        )}
        <Heading size="md" textAlign="center">
          Payment Status: {checkoutSession?.payment_status || 'Unknown'}
        </Heading>
        <Text textAlign="center">
          Amount: ${(checkoutSession?.amount_total || 0) / 100}{' '}
          {checkoutSession?.currency.toUpperCase()}
        </Text>
        <Text textAlign="center">Transaction ID: {checkoutSession?.id}</Text>
        {checkoutSession?.customer_details && (
          <>
            <Text textAlign="center">Name: {checkoutSession.customer_details.name}</Text>
            <Text textAlign="center">
              Email: {checkoutSession.customer_details.email}
            </Text>
          </>
        )}
        <Text textAlign="center">Status: {checkoutSession?.status}</Text>
        <Button as={Link} to="/payment" colorScheme="blue" mt={4}>
          Go Back to Payment
        </Button>
      </VStack>
    </Box>
  );
};

export const SuccessDisplay = ({ sessionId }: { sessionId: string }) => (
  <VStack spacing={4} align="stretch">
    <Heading size="md">Subscription Successful!</Heading>
    <Text>Thank you for subscribing to our Custom Plan.</Text>
    <Text>
      Your session ID:
      {sessionId}
    </Text>
    <Button colorScheme="green" onClick={() => (window.location.href = '/payment')}>
      Return to Dashboard
    </Button>
  </VStack>
);
